import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import {
  User,
  MapPin,
  Plus,
  Eye,
  EyeOff,
  Mail,
  Phone,
  Lock,
  Camera,
} from "lucide-react";
import axios from "axios";
import { Button, Alert, Modal, Form, Spinner } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";

import "../styles/AccountSettings.css";
import ProfilePictureModal from "../components/ProfileImageModal";
import AddNewAddress from "../components/AddNewAddress";
import EditAddress from "../components/EditAddress";
import SuccessModal from "../components/SuccessModal";
import ConfirmModal from "../components/ConfirmModal";
import CountryCodes from "../components/CountryCodes";

const BASE_URL = process.env.REACT_APP_API_URL;
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function AccountSettings() {
  const { userData } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("profile");
  const [showPassword, setShowPassword] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(null);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [modalEmail, setModalEmail] = useState("");
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [showAddNewAddress, setShowAddNewAddress] = useState(false);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successTitle, setSuccessTitle] = useState('Success');

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('Error');

  // Confirm modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('Confirm Action');
  const [confirmAction, setConfirmAction] = useState(null);

  const fileInputRef = useRef(null);

  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+94",
    password: "",
    profilePicture: "",
  });

  const sidebarItems = [
    { id: "profile", label: "Profile Information", icon: User },
    { id: "addresses", label: "Shipping Addresses", icon: MapPin },
    // { id: 'payment', label: 'Payment Methods', icon: CreditCard },
  ];

  // Helper functions for showing modals
  const showSuccess = (message, title = 'Success') => {
    setSuccessMessage(message);
    setSuccessTitle(title);
    setShowSuccessModal(true);
  };

  const hideSuccess = () => {
    setShowSuccessModal(false);
  };

  const showError = (message, title = 'Error') => {
    setErrorMessage(message);
    setErrorTitle(title);
    setShowErrorModal(true);
  };

  const hideError = () => {
    setShowErrorModal(false);
  };

  // Get auth token from logged in user
  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem("authToken");
    return token;
  }, []);

  // Get axios config with auth token
  const getAxiosConfig = useCallback(() => {
    const token = getAuthToken();
    const config = {
      params: { company_code: COMPANY_CODE },
      headers: {
        "Content-Type": "application/json",
      },
    };
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }, [getAuthToken]);

  // Fetch payment methods separately
  // const fetchPaymentMethods = async () => {
  //   setPaymentsLoading(true);
  //   setPaymentsError(null);
  //   try {
  //     const config = getAxiosConfig();
  //     const response = await axios.get(`${BASE_URL}/api/customer/payment-methods/get-payment-methods`, config);

  //     // Transform the data to match UI
  //     const formattedPayments = response.data.map(payment => ({
  //       id: payment.id,
  //       last4: payment.card_number ? payment.card_number.slice(-4) : '0000',
  //       cardHolder: `${payment.cardholder_name || 'N/A'}`,
  //       expiryDate: payment.expiry_month && payment.expiry_year
  //         ? `${payment.expiry_month.toString().padStart(2, '0')}/${payment.expiry_year.toString().slice(-2)}`
  //         : 'N/A',
  //       type: payment.card_type || 'Unknown',
  //       isDefault: payment.is_default === 1 || payment.is_default === true,
  //       brand: payment.card_brand || 'VISA'
  //     }));

  //     setPaymentMethods(formattedPayments);
  //   } catch (error) {
  //     console.error('Error fetching payment methods:', error);
  //     setPaymentsError('Failed to load payment methods');
  //     setPaymentMethods([]); // Set empty array on error
  //   } finally {
  //     setPaymentsLoading(false);
  //   }
  // };

  const fetchAddresses = useCallback(async () => {
    setAddressesLoading(true);
    setAddressError(null);
    try {
      const config = getAxiosConfig();
      const response = await axios.get(
        `${BASE_URL}/api/customer/address/get-address`,
        config
      );

      const formattedAddresses = response.data.map((addr, idx) => {
        const reactKeyBase =
          addr.id ??
          `${addr.address_line_1 || ""}-${addr.city || ""}-${
            addr.postal_code || ""
          }-${idx}`;
        return {
          id: addr.address_id, // keep the server id for setDefault/delete calls
          reactKey: `addr-${reactKeyBase}`,
          name: `${addr.first_name || ""} ${addr.last_name || ""}`.trim(),
          type: addr.address_type || "Shipping",
          house: addr.house || "",
          address: `${addr.address_line_1 || ""} ${
            addr.address_line_2 || ""
          }`.trim(),
          city: addr.city || "",
          state: addr.state || "",
          postalCode: addr.postal_code || "",
          country: addr.country || "",
          isDefault: addr.is_default === 1 || addr.is_default === true,
          phone: addr.phone || "",
          countryCode: addr.country_code || "+94",
        };
      });

      setAddresses(formattedAddresses);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setAddressError("Failed to load addresses");
      setAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  }, [getAxiosConfig]);

  const [loadingDefault, setLoadingDefault] = React.useState(null); // track which address is updating

  // Set default address function
  const setDefaultAddress = async (addressId) => {
    if (!addressId) return;

    setLoadingDefault(addressId); // disable btn for this address

    try {
      const config = getAxiosConfig();
      const response = await axios.post(
        `${BASE_URL}/api/customer/address/set-default-address`,
        {
          address_id: addressId,
          company_code: COMPANY_CODE,
        },
        config
      );

      if (response.status === 200) {
        // Mark selected address isDefault true, rest false
        setAddresses((prev) =>
          prev.map((addr) => ({
            ...addr,
            isDefault: addr.id === addressId,
          }))
        );

        showSuccess("Default address set successfully!", "Done");
      }
    } catch (error) {
      console.error(
        "Error setting default address:",
        error.response?.data?.message
      );
      showError("Failed to set default address. Please try again later.", "Error");
    } finally {
      setLoadingDefault(null);
    }
  };

  //delete address
  const deleteAddress = async (addressId) => {
    if (!addressId) return;

    setConfirmTitle('Delete Address');
    setConfirmMessage('Are you sure you want to delete this address?');
    setConfirmAction(() => () => deleteAddressConfirmed(addressId));
    setShowConfirmModal(true);
  };

  const deleteAddressConfirmed = async (addressId) => {
    try {
      const config = getAxiosConfig();
      const response = await axios.delete(
        `${BASE_URL}/api/customer/address/delete-address`,
        {
          data: { address_id: addressId },
          ...config,
        }
      );
      if (response.status === 200) {
        // Remove deleted address from state
        setAddresses((prev) =>
          prev.filter((addr) => addr.id !== addressId)
        );
        showSuccess("Address deleted successfully!", "Done");
      }
    } catch (error) {
      console.error(
        "Error deleting address:",
        error.response?.data?.message
      );
      showError(
        "This address cannot be deleted while an order is still being processed with it.",
        "Error"
      );
    }
  };

  // Get full profile image URL
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return null;

    // construct full URL
    return `${BASE_URL}/uploads/profile_images/${imagePath}`;
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        showError("Please select a valid image file (JPG, JPEG, PNG, WEBP).", "Invalid File Type");
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        showError(
          "The selected file exceeds the maximum size of 5MB. Please choose a smaller file.",
          "File Size Exceeded"
        );
        return;
      }
      setSelectedFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload profile picture
  const handleUploadProfilePicture = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("profile_image", selectedFile);

    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${BASE_URL}/api/customer/user/upload-profile-image`,
        formData,
        {
          params: { company_code: COMPANY_CODE },
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        }
      );

      // Update profile data with new profile picture URL
      setProfileData((prev) => ({
        ...prev,
        profilePicture: response.data.profile_image,
      }));

      // Reset states
      setSelectedFile(null);
      setPreviewUrl(null);
      setShowProfilePictureModal(false);
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      showError("Failed to upload profile picture. Please try again.", "Upload Failed");
    } finally {
      setIsUploading(false);
    }
  };

  // Delete profile picture
  const handleDeleteProfilePicture = async () => {
    if (!profileData.profilePicture) return;

    setConfirmTitle('Remove Profile Picture');
    setConfirmMessage('Are you sure you want to remove your existing profile picture?');
    setConfirmAction(() => () => handleDeleteProfilePictureConfirmed());
    setShowConfirmModal(true);
  };

  const handleDeleteProfilePictureConfirmed = async () => {
    try {
      const config = getAxiosConfig();
      await axios.delete(
        `${BASE_URL}/api/customer/user/delete-profile-image`,
        config
      );

      // Update profile data to remove profile picture
      setProfileData((prev) => ({
        ...prev,
        profilePicture: "",
      }));

      setShowProfilePictureModal(false);
      showSuccess("Your profile picture has been removed successfully.", "Done");
    } catch (error) {
      console.error("Error deleting profile picture:", error);
      showError("Failed to delete profile picture. Please try again.", "Can not delete profile picture.");
    }
  };

  // Reset file selection
  const resetFileSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpdateProfileDetails = async () => {
    const { firstName, lastName, email, phone, password } = profileData;
    if (!firstName || !lastName || !email || !phone) {
      showError("Please fill in all fields.", "Missing Information");
      return;
    }
    try {
      const cleanPhone = phone.replace(/\D/g, "");

      const config = getAxiosConfig();
      const response = await axios.put(
        `${BASE_URL}/api/customer/user/update-profile`,
        {
          first_name: firstName,
          last_name: lastName,
          email,
          country_code: profileData.countryCode,
          phone: cleanPhone,
          password,
          profile_image: profileData.profilePicture,
        },
        config
      );
      showSuccess("Update successfully", "Done");
      // Refresh the profile data after update
      setProfileData((prev) => ({
        ...prev,
        firstName,
        lastName,
        email,
        phone,
        password,
        profilePicture: response.data.profile_image || prev.profilePicture,
      }));
    } catch (error) {
      console.error("Error updating profile details:", error);
      showError("Failed to update profile details. Please try again.", "Update Failed");
    }
  };

  const handleForgotPassword = async (emailParam) => {
    setForgotMsg("");
    setForgotLoading(true);
    setForgotSuccess(null);
    try {
      const emailToUse =
        emailParam ||
        (userData && userData.email) ||
        (profileData && profileData.email);
      if (!emailToUse) {
        setForgotMsg("No email available.");
        setForgotSuccess(false);
        setForgotLoading(false);
        return;
      }

      const res = await fetch(
        `${BASE_URL}/api/customer/auth/request-password-reset`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            company_code: COMPANY_CODE,
            email: emailToUse,
            frontend_url: window.location.origin,
          }),
        }
      );

      const data = await res.json();
      if (res.ok && data.success) {
        setForgotMsg(
          "Password reset email sent to the registered email address."
        );
        setForgotSuccess(true);
      } else {
        setForgotMsg(data.message || "Failed to send reset email.");
        setForgotSuccess(false);
      }
    } catch (err) {
      console.error("Forgot password error", err);
      setForgotMsg("Something went wrong while requesting password reset.");
      setForgotSuccess(false);
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/customer/user/profile`,
          getAxiosConfig()
        );

        setProfileData({
          firstName: response.data.first_name || "",
          lastName: response.data.last_name || "",
          email: response.data.email || "",
          phone: response.data.phone || "",
          countryCode: response.data.country_code || "+94",
          password: response.data.password || "",
          profilePicture: response.data.profile_image || "",
        });
      } catch (error) {
        console.error("Error fetching account data:", error);
      }
    };

    fetchData();
  }, [getAxiosConfig]);

  // Fetch addresses when addresses tab is active
  useEffect(() => {
    if (activeTab === "addresses" && addresses.length === 0) {
      fetchAddresses();
    }
  }, [activeTab, addresses.length, fetchAddresses]);

  // Fetch payment methods when payment tab is active
  // useEffect(() => {
  //   if (activeTab === 'payment' && paymentMethods.length === 0) {
  //     fetchPaymentMethods();
  //   }
  // }, [activeTab]);

  const getInitials = () => {
    return `${profileData.firstName.charAt(0)}${profileData.lastName.charAt(
      0
    )}`;
  };

  // Get the display URL for profile picture
  const profileImageUrl = getProfileImageUrl(profileData.profilePicture);

  return (
    <>
      <div className="main-container">
        {/* Header */}
        <div className="header-section">
          <div
            className="profile-avatar-large clickable"
            onClick={() => setShowProfilePictureModal(true)}
            style={{
              backgroundImage: profileImageUrl
                ? `url(${profileImageUrl})`
                : "none",
              backgroundColor: profileImageUrl
                ? "transparent"
                : "hsl(266, 70%, 26%, 0.5)",
              color: profileImageUrl ? "transparent" : "white",
            }}
          >
            {!profileImageUrl && getInitials()}
            <div className="profile-picture-overlay">
              <Camera size={20} />
            </div>
          </div>

          <div>
            <h2>
              {profileData.firstName} {profileData.lastName}
            </h2>
            <p>Manage your account settings and preferences</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="nav-tabs-custom">
          <ul className="nav nav-tabs border-0">
            {sidebarItems.map((item) => (
              <li className="nav-item" key={item.id}>
                <button
                  className={`nav-link d-flex align-items-center ${
                    activeTab === item.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTab(item.id)}
                >
                  <item.icon size={18} className="me-2" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Content */}
        <div className="content-section">
          {/* Profile Tab */}
          {activeTab === "profile" && (
            <div>
              <h3 className="section-title">
                <User className="me-2" />
                Profile Information
              </h3>

              <div className="info-card">
                <h5 className="mb-4">Personal Details</h5>
                <div className="row">
                  <div className="col-md-6 form-group-custom">
                    <label className="form-label fw-bold">First Name</label>
                    <input
                      type="text"
                      className="form-control form-control-custom"
                      value={profileData.firstName}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-6 form-group-custom">
                    <label className="form-label fw-bold">Last Name</label>
                    <input
                      type="text"
                      className="form-control form-control-custom"
                      value={profileData.lastName}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-6 form-group-custom">
                    <label className="form-label fw-bold">
                      <Mail size={16} className="me-1" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-custom"
                      value={profileData.email}
                      onChange={(e) =>
                        setProfileData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="col-md-6 form-group-custom">
                    <label className="form-label fw-bold">
                      <Phone size={16} className="me-1" />
                      Phone Number
                    </label>
                    <div className="d-flex gap-2">
                      <CountryCodes
                        value={profileData.countryCode}
                        onChange={(code) =>
                          setProfileData((prev) => ({
                            ...prev,
                            countryCode: code,
                          }))
                        }
                        placeholder="Code"
                        isRequired={true}
                        width="100px"
                      />
                      <input
                        type="text"
                        className="form-control form-control-custom"
                        value={profileData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setProfileData((prev) => ({ ...prev, phone: value }));
                        }}
                        placeholder="Enter phone number"
                        style={{ flex: 1 }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <h5 className="mb-4">
                  <Lock size={20} className="me-2" />
                  Security Settings
                </h5>
                <div className="row">
                  <div className="col-md-6 form-group-custom">
                    <label className="form-label fw-bold">
                      Current Password
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control form-control-custom"
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div
                    className="col-md-6 form-group-custom"
                    style={{ position: "relative" }}
                  >
                    <Button
                      className="change-password-btn"
                      type="button"
                      onClick={() => {
                        const prefill =
                          userData && userData.email
                            ? userData.email
                            : profileData.email || "";
                        setModalEmail(prefill);
                        setForgotMsg("");
                        setShowForgotModal(true);
                      }}
                      disabled={forgotLoading}
                    >
                      {forgotLoading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Sending...
                        </>
                      ) : (
                        "Change Password"
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-3">
                <button
                  className="btn save-btn"
                  onClick={() => handleUpdateProfileDetails()}
                >
                  Save All Changes
                </button>
                <button className="btn btn-custom cancel-btn">Cancel</button>
              </div>
            </div>
          )}

          {/* Addresses Tab */}
          {activeTab === "addresses" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="section-title mb-0">
                  <MapPin className="me-2" />
                  Shipping Addresses
                </h3>
                <button
                  className="btn new-address-btn"
                  onClick={() => setShowAddNewAddress(true)}
                >
                  <Plus size={18} className="me-2" />
                  Add New Address
                </button>
              </div>

              {/* Loading State */}
              {addressesLoading && (
                <div className="info-card text-center">
                  <p>Loading addresses...</p>
                </div>
              )}

              {/* Error State */}
              {addressError && (
                <div className="info-card text-center">
                  <p className="text-danger">{addressError}</p>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchAddresses}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {/* No Addresses */}
              {!addressesLoading && !addressError && addresses.length === 0 && (
                <div className="info-card text-center">
                  <MapPin size={48} className="text-muted mb-3" />
                  <h5>No addresses found</h5>
                  <p className="text-muted">
                    Add your first shipping address to get started.
                  </p>
                  <button className="btn new-address-btn" onClick={() => setShowAddNewAddress(true)}>
                    <Plus size={18} className="me-2" />
                    Add Your First Address
                  </button>
                </div>
              )}

              {/* Address List */}
              <div className="row">
                {!addressesLoading &&
                  !addressError &&
                  addresses.length > 0 &&
                  addresses.map((address, index) => (
                    <div key={address.id || index} className="col-md-6 mb-4">
                      <div
                        className={`address-card ${
                          address.isDefault ? "default" : ""
                        }`}
                      >
                        {address.isDefault && (
                          <div className="default-badge">DEFAULT</div>
                        )}

                        <div className="row">
                          <div className="col-8 details-column">
                            <h5 className="mb-2">
                              <MapPin size={18} className="me-2 text-primary" />
                              {address.type} Address
                            </h5>
                            <h6 className="text-dark address-name">
                              {address.name}
                            </h6>
                            <p className="mb-1 address-house">
                              {address.house}
                            </p>
                            <p className="mb-1 address-lines">
                              {address.address}
                            </p>
                            <p className="mb-1 address-lines">{address.city}</p>
                            <p className="mb-1 address-lines">
                              {address.state}, {address.postalCode}
                            </p>
                            <p className="mb-0 address-lines">
                              {address.country}
                            </p>
                            {address.phone && (
                              <p className="mb-0 address-phone">
                                <Phone size={14} className="me-1" />
                                {address.countryCode} {address.phone}
                              </p>
                            )}
                          </div>
                          <div className="col-4 text-end">
                            <div className="d-flex flex-column gap-1">
                              {/* edit */}
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  setSelectedAddress(address);
                                  setShowEditAddress(true);
                                }}
                              >
                                Edit
                              </button>

                              {/* set default */}
                              {!address.isDefault && (
                                <button
                                  className="btn btn-outline-success btn-sm"
                                  onClick={() => setDefaultAddress(address.id)}
                                  disabled={loadingDefault === address.id}
                                >
                                  {loadingDefault === address.id
                                    ? "Setting..."
                                    : "Set as Default"}
                                </button>
                              )}

                              {/* delete */}
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => deleteAddress(address.id)}
                                // disabled={loadingDelete === address.id}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Payment Methods Tab */}
          {/* {activeTab === 'payment' && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="section-title mb-0">
                  <CreditCard className="me-2" />
                  Payment Methods
                </h3>
                <button className="btn new-card-btn">
                  <Plus size={18} className="me-2" />
                  Add New Card
                </button>
              </div> */}

          {/* Loading State */}
          {/* {paymentsLoading && (
                <div className="info-card text-center">
                  <p>Loading payment methods...</p>
                </div>
              )} */}

          {/* Error State */}
          {/* {paymentsError && (
                <div className="info-card text-center">
                  <p className="text-danger">{paymentsError}</p>
                  <button 
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchPaymentMethods}
                  >
                    Try Again
                  </button>
                </div>
              )} */}

          {/* No Payment Methods */}
          {/* {!paymentsLoading && !paymentsError && paymentMethods.length === 0 && (
                <div className="info-card text-center">
                  <CreditCard size={48} className="text-muted mb-3" />
                  <h5>No payment methods found</h5>
                  <p className="text-muted">Add your first payment method to make purchases easier.</p>
                  <button className="btn new-card-btn">
                    <Plus size={18} className="me-2" />
                    Add Your First Card
                  </button>
                </div>
              )} */}

          {/* Payment Methods List */}
          {/* {!paymentsLoading && !paymentsError && paymentMethods.length > 0 && paymentMethods.map((payment,index) => (
                <div key={payment.id || index} className={`payment-card ${payment.isDefault ? 'default' : ''}`}>
                  {payment.isDefault && <div className="default-badge">DEFAULT</div>}
                  
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="card-chip"></div>
                      <div className="card-number">
                        •••• •••• •••• {payment.last4}
                      </div>
                      <div className="d-flex justify-content-between">
                        <div>
                          <small className="opacity-75">CARD HOLDER</small>
                          <div className="fw-bold">{payment.cardHolder}</div>
                        </div>
                        <div className="text-end">
                          <small className="opacity-75">EXPIRES</small>
                          <div className="fw-bold">{payment.expiryDate}</div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className="badge bg-light text-dark">{payment.type}</span>
                        <span className="badge bg-primary text-white ms-2">{payment.brand}</span>
                      </div>
                    </div>
                    <div className="col-md-4 text-end">
                      <div className="d-flex flex-column gap-2">
                        <button className="btn btn-light btn-sm">
                          <Edit3 size={14} className="me-1" />
                          Edit
                        </button>
                        {!payment.isDefault && (
                          <button className="btn btn-outline-light btn-sm">
                            Set as Default
                          </button>
                        )}
                        <button className="btn btn-outline-light btn-sm">
                          <Trash2 size={14} className="me-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))} */}

          {/* <div className="info-card mt-4">
                <div className="d-flex align-items-center">
                  <Shield size={24} className="text-success me-3" />
                  <div>
                    <h6 className="mb-1">Your payments are secure</h6>
                    <small className="text-muted">
                      We use industry-standard encryption to protect your payment information. 
                      Your full card number is never stored on our servers.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>

      {/* Forgot Password Modal (used by Change Password button) */}
      <Modal
        show={showForgotModal}
        onHide={() => setShowForgotModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Forgot password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Enter email address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={modalEmail}
                onChange={(e) => setModalEmail(e.target.value)}
                placeholder="Enter your registered email"
              />
            </Form.Group>

            {forgotMsg && (
              <Alert variant={forgotSuccess ? "success" : "danger"}>
                {forgotMsg}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowForgotModal(false)}
            disabled={forgotLoading}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await handleForgotPassword(modalEmail.trim());
            }}
            disabled={forgotLoading}
          >
            {forgotLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              "Send Reset Link"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Profile Picture Modal */}
      <ProfilePictureModal
        show={showProfilePictureModal}
        onClose={() => setShowProfilePictureModal(false)}
        profileImageUrl={profileImageUrl}
        getInitials={getInitials}
        fileInputRef={fileInputRef}
        handleFileSelect={handleFileSelect}
        selectedFile={selectedFile}
        previewUrl={previewUrl}
        resetFileSelection={resetFileSelection}
        handleUploadProfilePicture={handleUploadProfilePicture}
        handleDeleteProfilePicture={handleDeleteProfilePicture}
        isUploading={isUploading}
      />

      {/* Add New Address Modal */}
      <AddNewAddress
        show={showAddNewAddress}
        onHide={() => setShowAddNewAddress(false)}
        onSubmit={(result) => {
          // Add the new address
          const newFormattedAddress = {
            id: result.addressId,
            reactKey: `addr-${result.addressId}`,
            name: `${result.addressData.first_name} ${result.addressData.last_name}`,
            type: "Shipping",
            house: result.addressData.house || "",
            address: `${result.addressData.address_line_1} ${
              result.addressData.address_line_2 || ""
            }`.trim(),
            city: result.addressData.city,
            state: result.addressData.state,
            postalCode: result.addressData.postal_code,
            country: result.addressData.country,
            isDefault: false,
            phone: result.addressData.phone,
          };

          setAddresses((prev) => [...prev, newFormattedAddress]);

          // Close the modal
          setShowAddNewAddress(false);
        }}
      />

      {/* Edit Address Modal */}
      <EditAddress
        show={showEditAddress}
        address={selectedAddress}
        onHide={() => {
          setShowEditAddress(false);
          setSelectedAddress(null);
        }}
        onSubmit={(result) => {
          // Update the address
          const updatedAddress = {
            id: result.addressId,
            reactKey: `addr-${result.addressId}`,
            name: `${result.addressData.first_name} ${result.addressData.last_name}`,
            type: "Shipping",
            house: result.addressData.house || "",
            address: `${result.addressData.address_line_1} ${
              result.addressData.address_line_2 || ""
            }`.trim(),
            city: result.addressData.city,
            state: result.addressData.state,
            postalCode: result.addressData.postal_code,
            country: result.addressData.country,
          };

          setAddresses((prev) =>
            prev.map((addr) =>
              addr.id === updatedAddress.id ? updatedAddress : addr
            )
          );

          // Close the modal
          setShowEditAddress(false);
          setSelectedAddress(null);
        }}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />

      {/* Error Modal */}
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />

      {/* Confirm Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Delete"
        confirmVariant="danger"
        cancelText="Cancel"
      />
    </>
  );
}
